import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { TABLES } from '../lib/constants';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiFacebook, FiLink, FiArrowLeft, FiPackage, FiChevronLeft, 
  FiChevronRight, FiCheckCircle, FiBox, FiClock, FiShare2, 
  FiHeart, FiMessageCircle, FiSend, FiUser, FiLock 
} = FiIcons;

const ITEMS_PER_PAGE = 12;

const PublicFeeds = () => {
  const { user } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [copyingId, setCopyingId] = useState(null);
  const [likedItems, setLikedItems] = useState(new Set());
  
  // Comment States
  const [activeCommentsId, setActiveCommentsId] = useState(null);
  const [comments, setComments] = useState({}); // { log_id: [comments] }
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchFeeds();
    const localLikes = JSON.parse(localStorage.getItem('rox_liked_ids') || '[]');
    setLikedItems(new Set(localLikes));
  }, [page]);

  const fetchFeeds = async () => {
    setLoading(true);
    try {
      const { count } = await supabase.from(TABLES.LOGS).select('*', { count: 'exact', head: true });
      setTotalCount(count || 0);

      const { data, error } = await supabase
        .from(TABLES.LOGS)
        .select(`
          id, created_at, proof_url, quantity, likes,
          pilot:pilot_id(username, nickname),
          item:item_id(name, category)
        `)
        .order('created_at', { ascending: false })
        .range(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE - 1);

      if (error) throw error;
      setFeeds(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (logId) => {
    const { data } = await supabase
      .from(TABLES.COMMENTS)
      .select(`*, user:${TABLES.USERS}(username, nickname)`)
      .eq('log_id', logId)
      .order('created_at', { ascending: true });
    setComments(prev => ({ ...prev, [logId]: data || [] }));
  };

  const handleToggleComments = (logId) => {
    if (activeCommentsId === logId) {
      setActiveCommentsId(null);
    } else {
      setActiveCommentsId(logId);
      if (!comments[logId]) fetchComments(logId);
    }
  };

  const handlePostComment = async (logId) => {
    if (!newComment.trim() || !user) return;
    setSubmittingComment(true);
    try {
      const { error } = await supabase.from(TABLES.COMMENTS).insert([{
        log_id: logId,
        user_id: user.id,
        content: newComment.trim()
      }]);
      if (error) throw error;
      setNewComment('');
      fetchComments(logId);
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLike = async (id, currentLikes) => {
    if (likedItems.has(id)) return;
    setFeeds(prev => prev.map(f => f.id === id ? { ...f, likes: (f.likes || 0) + 1 } : f));
    const newLikedItems = new Set([...likedItems, id]);
    setLikedItems(newLikedItems);
    localStorage.setItem('rox_liked_ids', JSON.stringify([...newLikedItems]));
    await supabase.from(TABLES.LOGS).update({ likes: (currentLikes || 0) + 1 }).eq('id', id);
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-[#f5f5f7] pb-24">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="#/login" className="p-2.5 bg-gray-100 rounded-2xl text-gray-500 hover:text-gray-900 transition-all active:scale-95">
              <SafeIcon icon={FiArrowLeft} size={20} />
            </a>
            <div>
              <h1 className="text-xl font-black text-gray-900 tracking-tight">Activity Feed</h1>
              <div className="flex items-center gap-1.5 text-[10px] font-black text-blue-500 uppercase tracking-widest">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" /> Live Stream
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3 px-5 py-2.5 bg-black text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
              <SafeIcon icon={FiBox} className="text-blue-400" /> {totalCount} Recorded
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-12">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-40 gap-4">
            <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {feeds.map((feed) => (
              <motion.div key={feed.id} layout className="flex flex-col">
                <div className="bento-card !p-0 overflow-hidden border-2 border-transparent hover:border-blue-100 transition-all hover:shadow-2xl flex flex-col h-full bg-white">
                  <div className="relative aspect-video bg-gray-50 overflow-hidden shrink-0">
                    {feed.proof_url ? (
                      <img src={feed.proof_url} alt={feed.item?.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-300">
                        <SafeIcon icon={FiPackage} size={32} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      <span className="px-2.5 py-1 bg-black/50 backdrop-blur-md text-white text-[8px] font-black uppercase rounded-full">
                        {feed.item?.category || 'General'}
                      </span>
                    </div>
                  </div>

                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="text-lg font-black text-gray-900 leading-tight mb-2">
                      {feed.item?.name} {feed.quantity > 1 && <span className="text-gray-300">x{feed.quantity}</span>}
                    </h3>
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-4">
                      Pilot: @{feed.pilot?.nickname || feed.pilot?.username}
                    </p>

                    <div className="flex items-center gap-4 mt-auto pt-4 border-t border-gray-50">
                      <button onClick={() => handleLike(feed.id, feed.likes)} className={`flex items-center gap-1.5 text-xs font-black ${likedItems.has(feed.id) ? 'text-red-500' : 'text-gray-400 hover:text-red-500'}`}>
                        <FiHeart className={likedItems.has(feed.id) ? 'fill-current' : ''} /> {feed.likes || 0}
                      </button>
                      <button onClick={() => handleToggleComments(feed.id)} className={`flex items-center gap-1.5 text-xs font-black ${activeCommentsId === feed.id ? 'text-blue-600' : 'text-gray-400 hover:text-blue-600'}`}>
                        <FiMessageCircle /> {comments[feed.id]?.length || 0}
                      </button>
                    </div>
                  </div>

                  {/* Comments Panel */}
                  <AnimatePresence>
                    {activeCommentsId === feed.id && (
                      <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden bg-gray-50 border-t border-gray-100">
                        <div className="p-5 space-y-4">
                          <div className="max-h-40 overflow-y-auto no-scrollbar space-y-3">
                            {(comments[feed.id] || []).map(c => (
                              <div key={c.id} className="bg-white p-3 rounded-xl border border-gray-100">
                                <p className="text-[8px] font-black text-blue-500 uppercase">@{c.user?.nickname || c.user?.username}</p>
                                <p className="text-xs text-gray-700 mt-1 font-medium">{c.content}</p>
                              </div>
                            ))}
                            {comments[feed.id]?.length === 0 && <p className="text-[9px] text-gray-400 font-black uppercase text-center py-2 italic">Be the first to comment</p>}
                          </div>
                          
                          {user ? (
                            <div className="flex gap-2">
                              <input className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs outline-none focus:ring-1 focus:ring-blue-500" placeholder="Write a comment..." value={newComment} onChange={e => setNewComment(e.target.value)} onKeyPress={e => e.key === 'Enter' && handlePostComment(feed.id)} />
                              <button onClick={() => handlePostComment(feed.id)} disabled={submittingComment} className="p-2.5 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-100"><FiSend /></button>
                            </div>
                          ) : (
                            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex items-center justify-center gap-2">
                              <FiLock className="text-blue-400 text-xs" />
                              <span className="text-[9px] font-black text-blue-600 uppercase tracking-widest">Login to comment</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PublicFeeds;